import { showApiErrors } from '@/utils';
import { createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import {
  getAppraisePrice,
  getSettlementData,
  updateAppraisePrice,
  getDocumentsPrice,
  updateDocumentsPrice,
  createPhotoShootingPrice,
  deletePhotoShootingPrice,
  getPhotoShootingPrice,
  updatePhotoShootingPrice,
  deleteAppraiserPrice,
  createAppraiserPrice,
  updateAppraiserPrice,
  updateSettlementData,
} from './actionCreators';
import { initialState, PRICES_SLICE_NAME, PricesState } from './models';

export const slice = createSlice({
  name: PRICES_SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateAppraisePrice.fulfilled, (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.appraisePrices = (state.appraisePrices ?? []).map((price) =>
          price.from_area === payload.from_area ? payload : price,
        );
      })
      .addCase(getAppraisePrice.fulfilled, (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.appraisePrices = payload;
      })
      .addCase(getPhotoShootingPrice.fulfilled, (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.photoShootingPrices = payload;
      })
      .addCase(createPhotoShootingPrice.fulfilled, (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.photoShootingPrices = (state.photoShootingPrices ?? []).concat(payload);
      })
      .addCase(updateSettlementData.fulfilled, (state: PricesState, action) => {
        state.isLoading = false;
        state.error = null;
        state.settlementData = action.payload;
      })
      .addCase(deletePhotoShootingPrice.fulfilled, (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.photoShootingPrices = (state.photoShootingPrices ?? []).filter((price) => price.PK !== payload.PK);
      })
      .addCase(updatePhotoShootingPrice.fulfilled, (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.photoShootingPrices = (state.photoShootingPrices ?? []).map((price) =>
          price.PK === payload.PK ? payload : price,
        );
      })
      .addCase(createAppraiserPrice.fulfilled, (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.appraisePrices = (state.appraisePrices ?? []).concat(payload);
      })
      .addCase(deleteAppraiserPrice.fulfilled, (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.appraisePrices = (state.appraisePrices ?? []).filter((price) => price.PK !== payload.PK);
      })
      .addCase(updateAppraiserPrice.fulfilled, (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.appraisePrices = (state.appraisePrices ?? []).map((price) => (price.PK === payload.PK ? payload : price));
      })
      .addMatcher(isFulfilled(getDocumentsPrice, updateDocumentsPrice), (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.price = payload;
      })
      .addMatcher(isFulfilled(getSettlementData, updateSettlementData), (state: PricesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.error = null;
        state.settlementData = payload;
      })
      .addMatcher(
        isPending(
          getAppraisePrice,
          getSettlementData,
          updateAppraisePrice,
          updateSettlementData,
          getDocumentsPrice,
          updateDocumentsPrice,
          createPhotoShootingPrice,
          deletePhotoShootingPrice,
          getPhotoShootingPrice,
          updatePhotoShootingPrice,
          updateSettlementData,
          createAppraiserPrice,
          deleteAppraiserPrice,
          updateAppraiserPrice,
        ),
        (state: PricesState) => {
          state.isLoading = true;
          state.error = null;
        },
      )
      .addMatcher(
        isRejected(
          getAppraisePrice,
          getSettlementData,
          updateAppraisePrice,
          getDocumentsPrice,
          updateDocumentsPrice,
          updateSettlementData,
          createPhotoShootingPrice,
          deletePhotoShootingPrice,
          getPhotoShootingPrice,
          updatePhotoShootingPrice,
          createAppraiserPrice,
          deleteAppraiserPrice,
          updateAppraiserPrice,
        ),
        (state: PricesState, action) => {
          const { error } = action;
          state.isLoading = false;
          state.error = error;

          showApiErrors(error);
        },
      );
  },
});

export default slice.reducer;
